import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FormField, mirrorFormRules, StatusPill } from './EMRNotes';
import { PatientAvatar } from './EMRPhotos';
import DictatePanel from './DictatePanel';

import { T } from './theme';
const API = 'https://tcio-api.onrender.com';
const card = { background:T.surface, border:`1px solid ${T.b200}`, borderRadius:10,
  boxShadow:'0 1px 2px rgba(110,59,71,.06), 0 4px 14px rgba(110,59,71,.07)' };
const label = { fontSize:10, letterSpacing:'1.1px', textTransform:'uppercase', color:T.inkSoft, fontWeight:600 };
const input = { border:`1px solid ${T.b200}`, borderRadius:8, padding:'8px 11px',
  font:'inherit', color:T.ink, background:'#fff', boxSizing:'border-box', width:'100%' };
const btn = (primary) => ({ borderRadius:8, padding:'9px 16px', font:'inherit', fontWeight:600,
  fontSize:13, cursor:'pointer', background: primary ? T.rose : '#fff',
  color: primary ? '#fff' : T.rose, border: primary ? 'none' : `1px solid ${T.b300}` });

export default function EMRGroups({ patients, prefill, onPrefillConsumed, user, onGoSchedule }) {
  // Chad 11/4: facilities flagged groups_ad_hoc_create=false (HBTS) run groups only from the Group Schedule.
  const [noAdHoc, setNoAdHoc] = useState([]);
  useEffect(() => { axios.get(`${API}/api/emr/groups/policy`).then(r => setNoAdHoc((r.data && r.data.no_ad_hoc) || [])).catch(() => {}); }, []);
  const [stage, setStage] = useState('setup'); // setup | roster
  const [facility, setFacility] = useState('');
  const [house, setHouse] = useState('ALL');
  const [topic, setTopic] = useState('');
  const [curriculum, setCurriculum] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0,10));
  const [startTime, setStartTime] = useState('13:00');
  const [duration, setDuration] = useState(60);
  const [checked, setChecked] = useState({});   // patient_id -> true (in group)
  const [absent, setAbsent] = useState({});     // patient_id -> true (marked absent)
  const [created, setCreated] = useState(null); // { group_session_id, notes:[{patient_id, note_id}] }
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  // roster editing state
  const [girpForm, setGirpForm] = useState(null);
  const [activeNote, setActiveNote] = useState(null); // {note_id, patient, content, status, gate}
  const [noteStatuses, setNoteStatuses] = useState({}); // note_id -> status
  const [dictBusy, setDictBusy] = useState(false);
  const [dictResult, setDictResult] = useState(null); // {drafted, unmentioned, failed, unmatched}
  const [schedInstanceId, setSchedInstanceId] = useState(null);
  useEffect(() => {
    if (!prefill) return;
    if (prefill.openInstanceId) {
      (async () => {
        try {
          const r = await axios.get(`${API}/api/emr/group-schedule/instances/${prefill.openInstanceId}/session`);
          const inst = r.data.instance; const clients = r.data.clients || [];
          const pm = patients.find(p => p.facility_id === inst.facility_id);
          if (pm) setFacility(pm.facility_name);
          setHouse('ALL');
          setTopic(inst.topic || '');
          setCurriculum(inst.description || '');
          setDate(String(inst.session_date_text || inst.session_date).slice(0, 10));
          setStartTime(String(inst.start_time).slice(0, 5));
          setDuration(inst.duration_minutes || 60);
          const c = {}; const st = {}; const notes = [];
          clients.forEach(cl => {
            c[cl.patient_id] = true;
            if (cl.note) { notes.push({ patient_id: cl.patient_id, note_id: cl.note.note_id }); st[cl.note.note_id] = cl.note.status; }
          });
          setChecked(c); setAbsent({}); setNoteStatuses(st); setActiveNote(null); setDictResult(null);
          if (inst.group_session_id) setCreated({ group_session_id: inst.group_session_id, notes });
          setStage('roster');
        } catch (e2) { setErr('Could not open group'); }
      })();
      if (onPrefillConsumed) onPrefillConsumed();
      return;
    }
    if (prefill.facility_name) setFacility(prefill.facility_name);
    else if (prefill.facility_id) { const pm = patients.find(p => p.facility_id === prefill.facility_id); if (pm) setFacility(pm.facility_name); }
    setHouse(prefill.house_name || 'ALL');
    setTopic(prefill.topic || '');
    setCurriculum(prefill.curriculum || '');
    if (prefill.date) setDate(prefill.date);
    if (prefill.startTime) setStartTime(prefill.startTime);
    if (prefill.duration) setDuration(prefill.duration);
    const c = {};
    (prefill.patientIds || []).forEach(id => { c[id] = true; });
    setChecked(c);
    setSchedInstanceId(prefill.instanceId || null);
    if (onPrefillConsumed) onPrefillConsumed();
  }, [prefill]); // eslint-disable-line react-hooks/exhaustive-deps

  const facilityNames = [...new Set(patients.map(p => p.facility_name).filter(Boolean))].sort(
    (a,b) => a.localeCompare(b));
  const facPatients = patients.filter(p => p.facility_name === facility && p.episode_id);
  const houseNames = [...new Set(facPatients.map(p => p.house_name).filter(Boolean))].sort();
  const roster = facPatients.filter(p => house === 'ALL' || p.house_name === house);
  const selCount = roster.filter(p => checked[p.id]).length;

  useEffect(() => { (async () => {
    try { setGirpForm((await axios.get(`${API}/api/emr/forms/group_individual_girp`)).data); } catch {}
  })(); }, []);

  const toggleAll = (on) => {
    const next = { ...checked };
    for (const p of roster) next[p.id] = on;
    setChecked(next);
  };

  const createGroup = async () => {
    const parts = roster.filter(p => checked[p.id]).map(p => ({
      patient_id: p.id, episode_id: p.episode_id, attending: !absent[p.id] }));
    if (!facility || !parts.length) { setErr('Pick a facility and at least one client'); return; }
    setBusy(true); setErr('');
    try {
      const fid = facPatients[0].facility_id;
      const hid = house !== 'ALL' ? null : null;
      const started = new Date(`${date}T${startTime}:00`).toISOString();
      const r = await axios.post(`${API}/api/emr/group-sessions`, {
        facility_id: fid, house_id: hid, session_date: date, started_at: started,
        duration_minutes: Number(duration) || null, topic,
        curriculum_narrative: curriculum, participants: parts });
      setCreated(r.data);
      if (schedInstanceId && r.data.group_session_id) {
        axios.post(`${API}/api/emr/group-schedule/instances/${schedInstanceId}/complete`,
          { group_session_id: r.data.group_session_id }).catch(() => {});
        setSchedInstanceId(null);
      }
      const st = {};
      for (const n of r.data.notes) st[n.note_id] = 'in_review';
      setNoteStatuses(st);
      setStage('roster');
    } catch (e2) { setErr((e2.response && e2.response.data && e2.response.data.error) || 'Create failed'); }
    setBusy(false);
  };

  const openNote = async (noteId, patient) => {
    setBusy(true); setErr('');
    try {
      const d = (await axios.get(`${API}/api/emr/notes/${noteId}`)).data;
      const cur = d.versions.find(v => v.id === d.note.current_version_id) || d.versions[0];
      setActiveNote({ note_id: noteId, patient, status: d.note.status,
        content: (cur && cur.content) || {}, gate: null });
    } catch { setErr('Could not open note'); }
    setBusy(false);
  };

  const setField = (k, v) => setActiveNote(a => ({ ...a, content: { ...a.content, [k]: v } }));
  const [saveMsg, setSaveMsg] = useState('');

  const saveAndSubmit = async (thenSign) => {
    if (!activeNote) return;
    setBusy(true); setErr(''); setSaveMsg('Saving\u2026');
    try {
      await axios.post(`${API}/api/emr/notes/${activeNote.note_id}/versions`,
        { content: activeNote.content, source:'clinician_edit' });
      setSaveMsg('Running compliance gate\u2026');
      const r = await axios.post(`${API}/api/emr/notes/${activeNote.note_id}/submit`);
      setActiveNote(a => ({ ...a, status: r.data.status, gate: r.data }));
      setNoteStatuses(s => ({ ...s, [activeNote.note_id]: r.data.status }));
      let finalMsg = 'Saved & gated';
      if (thenSign && r.data.status === 'pending_compliance_gate') {
        setSaveMsg('Signing\u2026');
        const signBody = { method:'attestation', attestation_text:'I attest that this documentation is accurate and complete.' };
        let s2;
        try {
          s2 = await axios.post(`${API}/api/emr/notes/${activeNote.note_id}/sign`, signBody);
        } catch (se) {
          const sd = se.response && se.response.data;
          if (se.response && se.response.status === 401 && sd && sd.pin_required) {
            // Signer has an enrolled signature PIN - collect it and retry as a PIN signature.
            const pin = window.prompt('Enter your 4-digit signature PIN to sign:');
            if (pin === null) throw new Error('Signing cancelled - PIN not entered');
            if (!/^\d{4}$/.test(pin.trim())) throw new Error('PIN must be exactly 4 digits');
            s2 = await axios.post(`${API}/api/emr/notes/${activeNote.note_id}/sign`,
              { method:'pin', pin: pin.trim(), attestation_text:'I attest that this documentation is accurate and complete.' });
          } else { throw se; }
        }
        setActiveNote(a => ({ ...a, status: s2.data.status }));
        setNoteStatuses(s => ({ ...s, [activeNote.note_id]: s2.data.status }));
        finalMsg = 'Saved & signed';
      } else if (thenSign) {
        finalMsg = 'Saved \u2014 gate blocked signing';
      }
      setSaveMsg('\u2713 ' + finalMsg + ' ' + new Date().toLocaleTimeString());
    } catch (e2) {
      setSaveMsg('');
      setErr((e2.response && e2.response.data && e2.response.data.error) || e2.message || 'Save failed');
    }
    setBusy(false);
  };

  const runDictation = async (text) => {
    if (!created || !text.trim()) return;
    setDictBusy(true); setErr(''); setDictResult(null);
    try {
      const r = await axios.post(`${API}/api/emr/group-sessions/${created.group_session_id}/ai-draft`,
        { transcript: text });
      setDictResult(r.data);
      if (activeNote) {
        const d = (await axios.get(`${API}/api/emr/notes/${activeNote.note_id}`)).data;
        setActiveNote(a => ({ ...a, content: (d.current_version && d.current_version.content) || a.content, status: d.status }));
      }
    } catch (e2) { setErr((e2.response && e2.response.data && e2.response.data.error) || 'AI draft failed'); }
    setDictBusy(false);
  };

  const mirror = (activeNote && girpForm) ? mirrorFormRules(girpForm, activeNote.content) : [];
  const mBlock = mirror.filter(r => !r.passed && r.severity === 'blocking').length;
  const locked = activeNote && ['finalized','amended','pending_cosign'].includes(activeNote.status);
  const notesByPatient = created ? Object.fromEntries(created.notes.map(n => [n.patient_id, n.note_id])) : {};

  // ── SETUP ──
  const isAdminUser = ['ceo', 'admin', 'super_admin'].includes(String((user && user.role) || ''));
  const facIdsHere = Array.from(new Set((patients || []).map(p => p.facility_id).filter(Boolean)));
  const adHocBlocked = !isAdminUser && noAdHoc.length > 0 && !prefill &&
    ((facility && noAdHoc.includes(facility)) || (!facility && facIdsHere.length > 0 && facIdsHere.every(id => noAdHoc.includes(id))));
  if (stage === 'setup' && !created && adHocBlocked) return (
    <div style={{ maxWidth: 720, margin: '24px auto', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, padding: 28 }}>
      <div style={{ fontSize: 18, fontWeight: 800, color: '#0f172a', marginBottom: 8 }}>Groups run from the Group Schedule</div>
      <div style={{ fontSize: 14, color: '#475569', lineHeight: 1.5, marginBottom: 18 }}>
        At this facility every group is already on the schedule with its roster filled from the census.
        Open <b>Group Schedule</b>, click your group, and press <b>Start Session</b> — the notes are created for everyone on the roster.
        Mark anyone who wasn't there as <b>Attended: No</b> with the reason.
      </div>
      <button onClick={() => onGoSchedule && onGoSchedule()} style={{ padding: '10px 18px', borderRadius: 10, border: 'none', background: '#2563eb', color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>Open Group Schedule →</button>
    </div>
  );
  if (stage === 'setup') return (
    <div style={{ ...card, padding:20, maxWidth:980 }}>
      <div style={{ fontSize:19, fontWeight:600, color:T.roseDeep, marginBottom:16 }}>
        New Group Session</div>
      {err && <div style={{ padding:'9px 13px', background:T.blockingBg, color:T.blocking,
        borderRadius:8, fontSize:13, fontWeight:600, marginBottom:12 }}>{err}</div>}
      <div style={{ display:'flex', gap:12, flexWrap:'wrap', marginBottom:14 }}>
        <div style={{ minWidth:220 }}><div style={{ ...label, marginBottom:4 }}>Facility</div>
          <select style={input} value={facility} onChange={e => { setFacility(e.target.value); setHouse('ALL'); setChecked({}); }}>
            <option value="">Select…</option>
            {facilityNames.map(f => <option key={f} value={f}>{f}</option>)}
          </select></div>
        <div style={{ minWidth:180 }}><div style={{ ...label, marginBottom:4 }}>House</div>
          <select style={input} value={house} onChange={e => setHouse(e.target.value)} disabled={!facility}>
            <option value="ALL">All houses</option>
            {houseNames.map(h => <option key={h} value={h}>{h}</option>)}
          </select></div>
        <div><div style={{ ...label, marginBottom:4 }}>Date</div>
          <input type="date" style={{ ...input, width:160 }} value={date} onChange={e => setDate(e.target.value)} /></div>
        <div><div style={{ ...label, marginBottom:4 }}>Start</div>
          <input type="time" style={{ ...input, width:120 }} value={startTime} onChange={e => setStartTime(e.target.value)} /></div>
        <div><div style={{ ...label, marginBottom:4 }}>Minutes</div>
          <input type="number" style={{ ...input, width:90 }} value={duration} onChange={e => setDuration(e.target.value)} /></div>
      </div>
      <div style={{ marginBottom:14 }}><div style={{ ...label, marginBottom:4 }}>Topic</div>
        <input style={input} placeholder="e.g. Relapse Prevention" value={topic} onChange={e => setTopic(e.target.value)} /></div>
      <div style={{ marginBottom:16 }}><div style={{ ...label, marginBottom:4 }}>Group description / curriculum (shared across all notes)</div>
        <textarea style={{ ...input, minHeight:110 }} value={curriculum} onChange={e => setCurriculum(e.target.value)}
          placeholder="What the group covered — video, discussion, worksheet, activities…" /></div>

      {facility && (
        <div style={{ marginBottom:16 }}>
          <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:8 }}>
            <strong>Clients ({selCount}/{roster.length})</strong>
            <button style={{ ...btn(false), padding:'4px 10px', fontSize:12 }} onClick={() => toggleAll(true)}>All</button>
            <button style={{ ...btn(false), padding:'4px 10px', fontSize:12 }} onClick={() => toggleAll(false)}>None</button>
            <span style={{ fontSize:12, color:T.inkSoft }}>Toggle "out" if scheduled but absent — an absence note is drafted, editable until signed.</span>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(230px, 1fr))', gap:8 }}>
            {roster.map(p => (
              <div key={p.id} style={{ display:'flex', gap:9, alignItems:'center', padding:'7px 9px',
                borderRadius:8, border:`1px solid ${checked[p.id] ? T.rose : T.b200}`,
                background: checked[p.id] ? T.b100 : '#fff' }}>
                <input type="checkbox" checked={!!checked[p.id]}
                  onChange={e => setChecked({ ...checked, [p.id]: e.target.checked })} />
                <PatientAvatar patient={p} size={34} radius={7} />
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight:600, fontSize:12.5, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                    {p.last_name}, {p.first_name}</div>
                  <div style={{ fontSize:10.5, color:T.inkSoft }}>{p.house_name || '—'}</div>
                </div>
                {checked[p.id] && (
                  <span onClick={() => setAbsent({ ...absent, [p.id]: !absent[p.id] })}
                    style={{ cursor:'pointer', fontSize:10.5, fontWeight:600, borderRadius:99, padding:'2px 8px',
                      background: absent[p.id] ? T.warning : T.passBg,
                      color: absent[p.id] ? '#fff' : T.pass }}>
                    {absent[p.id] ? 'OUT' : 'IN'}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      <button style={btn(true)} disabled={busy || !selCount} onClick={createGroup}>
        {busy ? 'Creating…' : `Create Group — ${selCount} note${selCount === 1 ? '' : 's'}`}</button>
    </div>
  );

  // ── ROSTER / EDITOR ──
  return (
    <div style={{ display:'grid', gridTemplateColumns:'270px 1fr', gap:16, alignItems:'start' }}>
      <div style={{ ...card, padding:14 }}>
        <div style={{ fontWeight:600, color:T.roseDeep, marginBottom:4 }}>{topic || 'Group'}</div>
        <div style={{ fontSize:12, color:T.inkSoft, marginBottom:12 }}>{(() => {
          const [y,m,d] = String(date).slice(0,10).split('-');
          const f12 = (t) => { const [h,mm] = String(t).slice(0,5).split(':').map(Number);
            return `${((h+11)%12)+1}:${String(mm).padStart(2,'0')} ${h>=12?'PM':'AM'}`; };
          const [sh,sm] = String(startTime).slice(0,5).split(':').map(Number);
          const em = sh*60+sm+(Number(duration)||60);
          const endT = `${String(Math.floor(em/60)%24).padStart(2,'0')}:${String(em%60).padStart(2,'0')}`;
          return `${m}/${d}/${y} · ${f12(startTime)}–${f12(endT)} (${duration} min)`;
        })()}</div>
        {roster.filter(p => checked[p.id]).map(p => {
          const nid = notesByPatient[p.id];
          const st = noteStatuses[nid] || 'in_review';
          return (
            <div key={p.id} onClick={() => nid && openNote(nid, p)}
              style={{ display:'flex', gap:8, alignItems:'center', padding:'7px 8px', borderRadius:8,
                cursor:'pointer', marginBottom:4,
                background: activeNote && activeNote.patient.id === p.id ? T.b100 : 'transparent' }}>
              <PatientAvatar patient={p} size={30} radius={6} />
              <div style={{ flex:1, minWidth:0, fontSize:12.5, fontWeight:600,
                whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                {p.last_name}, {p.first_name}</div>
              <StatusPill status={st} />
            </div>
          );
        })}
        <div style={{ marginTop:12, paddingTop:12, borderTop:`1px solid ${T.b200}` }}>
          <DictatePanel busy={dictBusy} onSubmit={runDictation}
            result={dictResult && (
              <div style={{ marginTop:10, fontSize:12 }}>
                <div style={{ color:T.pass, fontWeight:600 }}>&#10003; {dictResult.drafted.length} drafted</div>
                {dictResult.drafted.some(d => (d.conflicts || []).length > 0) && (
                  <div style={{ color:T.blocking, fontWeight:600, marginTop:4 }}>
                    Attendance check: {dictResult.drafted.filter(d => (d.conflicts || []).length > 0).map(d => d.name).join(', ')} — dictation didn't match the IN/OUT setting. Review before signing.</div>)}
                {dictResult.unmentioned.length > 0 && (
                  <div style={{ color:T.warning, marginTop:4 }}>
                    Not mentioned: {dictResult.unmentioned.map(u => u.name).join(', ')}</div>)}
                {dictResult.unmatched.length > 0 && (
                  <div style={{ color:T.blocking, marginTop:4 }}>
                    Couldn't match: {dictResult.unmatched.map(u => u.claimed || '?').join(', ')}</div>)}
                {dictResult.failed.length > 0 && (
                  <div style={{ color:T.blocking, marginTop:4 }}>
                    Failed: {dictResult.failed.map(f => f.name + (f.error ? ' — ' + f.error : '')).join('; ')}</div>)}
                <div style={{ color:T.inkSoft, marginTop:6 }}>Open each client to review, edit, and sign.</div>
              </div>
            )} />
        </div>
        <button style={{ ...btn(false), width:'100%', marginTop:10 }} onClick={() => { setStage('setup'); setActiveNote(null); setCreated(null); }}>
          ← New group</button>
      </div>

      <div style={{ ...card, padding:18, minHeight:300 }}>
        {err && <div style={{ padding:'9px 13px', background:T.blockingBg, color:T.blocking,
          borderRadius:8, fontSize:13, fontWeight:600, marginBottom:12 }}>{err}</div>}
        {!activeNote ? (
          <div style={{ color:T.inkSoft }}>Select a client to document their individual note. The shared curriculum is already on every note.</div>
        ) : (
          <div style={{ display:'grid', gridTemplateColumns:'1fr 230px', gap:16 }}>
            <div>
              <div style={{ display:'flex', gap:10, alignItems:'center', marginBottom:12 }}>
                <PatientAvatar patient={activeNote.patient} size={40} radius={8} />
                <strong style={{ fontSize:17 }}>
                  {activeNote.patient.last_name}, {activeNote.patient.first_name}</strong>
                <StatusPill status={activeNote.status} />
              </div>
              {activeNote.gate && activeNote.gate.status === 'kicked_back' && (
                <div style={{ background:T.blockingBg, border:`1px solid ${T.blocking}`, borderRadius:8,
                  padding:'10px 13px', marginBottom:12 }}>
                  {activeNote.gate.results.filter(r => !r.passed && r.severity === 'blocking').map(r => (
                    <div key={r.rule_code} style={{ fontSize:12.5, color:T.blocking }}>• {r.rule_description}</div>))}
                </div>
              )}
              {girpForm && girpForm.fields.map(f =>
                <FormField key={f.key} f={f} content={activeNote.content} locked={locked} setField={setField} />)}
              {!locked && (
                <div style={{ display:'flex', gap:10, marginTop:8 }}>
                  <button style={btn(false)} disabled={busy} onClick={() => saveAndSubmit(false)}>Save + Gate</button>
                  <button style={btn(true)} disabled={busy} onClick={() => saveAndSubmit(true)}>Save + Gate + Sign</button>
                  {saveMsg && <span style={{ marginLeft: 10, fontSize: 13, color: saveMsg.startsWith('\u2713') ? '#2e7d32' : '#8a6d3b' }}>{saveMsg}</span>}
                </div>
              )}
              {activeNote.status === 'finalized' &&
                <div style={{ marginTop:10, color:T.pass, fontWeight:600, fontSize:13 }}>✓ Finalized</div>}
            </div>
            <div style={{ background:T.bg, borderRadius:8, padding:14 }}>
              <div style={{ ...label, marginBottom:8 }}>Compliance</div>
              <div style={{ fontSize:12, fontWeight:600, marginBottom:10,
                color: mBlock ? T.blocking : T.pass }}>{mBlock} blocking open</div>
              {mirror.map(r => (
                <div key={r.code} style={{ display:'flex', gap:6, alignItems:'baseline', marginBottom:6 }}>
                  <span style={{ color: r.passed ? T.pass : T.blocking, fontWeight:600 }}>{r.passed ? '✓' : '✕'}</span>
                  <span style={{ fontSize:12, color: r.passed ? T.inkSoft : T.ink }}>{r.desc}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
